import { useLocation } from 'react-router-dom';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MESSAGES, availableValveParameterIds, availableActuatorParameterIds } from 'src/modules/common/constants.ts';
import { ConfigContext } from 'src/configContext.ts';
import { DeliveryType } from 'src/modules/checkout/components/DeliveryType.tsx';
import { RegionsEnum } from 'src/generated/operations.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { Button } from 'src/common/Button/Button.tsx';
import { RuntimeContext } from 'src/runtimeContext';
import { calculateTotals } from 'src/modules/checkout/CalculateTotals.ts';
import { addToCart } from 'src/utils/addToCart.ts';
import {
    CheckoutPageData,
} from 'src/modules/checkout/CheckoutPageMapping.ts';
import { prepareComboAttributes } from 'src/modules/checkout/prepareComboAttributes.ts';
import { ComboVariantEnum } from 'src/modules/common/ComboVariantEnum.ts';
import { Device } from 'src/common/Device/Device';
import { SubTotal } from 'src/common/SubTotal/SubTotal';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { SelectedProducts } from 'src/modules/common/components/SelectedProducts/SelectedProducts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { Counter } from 'src/common/Counter/Counter';
import { Accessory } from 'src/common/Accessory/Accessory';
import { Total } from 'src/common/Total/Total';
import { Price } from 'src/common/Price/Price';
import { NavigateFunction } from 'react-router/dist/lib/hooks';
import { decodeHtml } from 'src/utils/decodeHtml.ts';
import { checkoutViewOnly } from 'src/utils/isCheckoutEnabled';
import { InlineNotification } from 'src/modules/common/components/InlineNotification/InlineNotification';
import { useUserInputState } from 'src/modules/user-input/useUserInputState';
import { fetchDesignData } from 'src/common/fetchDesignData.ts';
import { useApolloClient } from '@apollo/client';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate.ts';
import { buildDesignDataContentRaw } from 'src/modules/common/hooks/buildDesignDataContent.ts';
import { convertVnomMetric } from 'src/modules/common/utils/convertVnom.ts';
import { getUnitByName } from 'src/modules/user-input/getUnitByName.ts';
import { getTargetUnits } from 'src/modules/user-input/getTargetUnits.ts';
import { useDefaultProject } from 'src/projects/hooks/useDefaultProject';
import { ValveBreadcrumbs } from 'src/modules/common/components/ValveBreadcrumbs/ValveBreadcrumbs';
import { PATHS } from 'src/types/Paths';
import clsx from 'clsx';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { CartIcon } from 'src/modules/common/icons/CartIcon';
import { prepareValvesSelectionExportTable } from 'src/modules/checkout/prepareExportData.ts';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';
import { generateRevitPluginXML } from 'src/modules/common/utils/generateRevitPluginXML';
import { SelectNominalVoltage, SelectValvePosition } from 'src/modules/checkout/types';
import { SelectVoltage } from 'src/modules/checkout/components/SelectVoltage';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { ValveDownloadXMLButton } from 'src/modules/checkout/components/ValveDownloadXMLButton';
import { CheckoutComment } from 'src/common/CheckoutComment/CheckoutComment';
import { SelectPosition } from 'src/modules/checkout/components/SelectPosition';
import { PipeConnectorsView } from 'src/common/PipeConnectorsView/PipeConnectorsView.tsx';
import { EDIT_SELECTION_PARAM } from 'src/common/constants.ts';
import { SaveIcon } from 'src/modules/common/icons/SaveIcon.tsx';
import { AddToListIcon } from 'src/modules/common/icons/AddToListIcon.tsx';

export const CheckoutPage = ({ data, navigate, homePageValue } : {
    navigate: NavigateFunction;
    data: CheckoutPageData;
    homePageValue: string;
}) => {
    const translate = useBelimoTranslate();
    const { state: userInputState, searchParams, locationState, setLocationState } = useUserInputState([]);
    const [inProgress, setInProgress] = useState<boolean>(false);
    const { userSettings, currency, gaPush } = useContext(ConfigContext);
    const { isMobile, isRevitPlugin } = useContext(RuntimeContext);
    const { state: { selectionRepository, persistCallback } } = useDefaultProject();
    const location = useLocation();
    // const { selectionRepository, persistCallback } = useDefaultProject();

    const editSelectionName = searchParams.get(EDIT_SELECTION_PARAM) ?? '';
    const isEditMode = editSelectionName !== '';

    const [nominalVoltage, setNominalVoltage] = useState<SelectNominalVoltage>('24 V');
    const [valvePosition, setValvePosition] = useState<SelectValvePosition>(SelectValvePosition.LM);

    const prepareVnom = (vnom: string) => convertVnomMetric(
        vnom,
        getUnitByName(userInputState.v100_u, getTargetUnits(userSettings.currentRegion).volumetricFlow),
        translate,
    );

    const isVoltagePresent = data.vssAccessories.actuator?.nominalVoltage?.raw.includes('24 V') &&
        data.vssAccessories.actuator?.nominalVoltage?.raw.includes('230 V');

    const is3WayButterflyValveSelected = data.vssAccessories.valve.pattern?.raw.includes('3-way') &&
        data.vssAccessories.valve.family?.raw.includes('Butterfly valve');

    let variantDecoded = ComboVariantEnum.Unmounted;
    if (data && data.vssCombo.length > 1) {
        variantDecoded = ComboVariantEnum.Both;
    }
    if (data && data.vssCombo.length === 1) {
        const [p] = data.vssCombo;
        if (p.Label.indexOf('+') > 0) {
            variantDecoded = ComboVariantEnum.Mounted;
        }
    }

    const defaultMountedValue = useMemo(() => userSettings.currentRegion === RegionsEnum.Am, [userSettings]);

    const comboCurrent = useMemo(() => locationState.combo ?? {
        mounted: variantDecoded === ComboVariantEnum.Both
            ? defaultMountedValue
            : variantDecoded === ComboVariantEnum.Mounted,
        qty: 1,
        actuator: locationState.actuatorLabel,
        valve: locationState.valveLabel,
    }, [variantDecoded, defaultMountedValue, locationState]);

    useEffect(() => {
        if (locationState.combo === null) {
            setLocationState({
                ...locationState,
                combo: comboCurrent,
            });
        }
        if (locationState.combo?.qty === null) {
            setLocationState({
                ...locationState,
                combo: { ...comboCurrent, qty: 1 },
            });
        }
    }, [comboCurrent, locationState, setLocationState]);

    const setDeliveryType = useCallback((mounted: boolean) => {
        setLocationState({
            ...locationState,
            combo: { ...comboCurrent, mounted },
        });
    }, [locationState, comboCurrent, setLocationState]);

    const setComboQty = useCallback((value: number) => {
        setLocationState({
            ...locationState,
            combo: { ...comboCurrent, qty: value },
        });
    }, [locationState, comboCurrent, setLocationState]);

    const selectMoreProductsHandler = useCallback(() => {
        navigate({
            pathname: PATHS.Home,
        });
    }, [navigate]);

    const { valve, actuator } = data.vssAccessories;

    const comboCandidate = data.vssCombo.find((item) => item.Label.includes(comboCurrent.mounted ? '+' : '/'));
    const combo = comboCandidate ?? valve;

    const {
        pricePerUnit,
        currentCurrency,
        total,
        comboSubtotal,
        pipeConnectors,
        pipeConnectorsSubtotal,
        valveAccessoriesSubtotal,
        valveAccessories,
        actuatorAccessoriesSubtotal,
        actuatorAccessories,
    } = useMemo(() => calculateTotals(
        combo,
        data.vssAccessories,
        userSettings,
        { ...locationState, combo: comboCurrent },
        currency,
        locationState.pipeConnectors,
    ), [locationState, comboCurrent, combo, currency, data.vssAccessories, userSettings]);

    const isDeliveryTypeVisible = locationState.valveLabel !== locationState.actuatorLabel &&
        actuator && userSettings.currentRegion !== RegionsEnum.Am;
    const lastSectionShown = isDeliveryTypeVisible || pipeConnectors.length > 0 || (
        isRevitPlugin && is3WayButterflyValveSelected) || (isRevitPlugin && isVoltagePresent);

    const isCombined = locationState.valveLabel === locationState.actuatorLabel;
    const comboName = `${
        comboCurrent.valve
    }${
        pipeConnectors.length
            ? `+${data.vssAccessories.valve.pipeConnectors?.localized.join(',')}` : ''
    }${
        is3WayButterflyValveSelected
            ? ` (${valvePosition})` : ''
    }`;
    const actuatorName = actuator
        ? `${actuator.Label}${isVoltagePresent ? ` (${nominalVoltage})` : ''}`
        : null;

    const setAccessoryQty = useCallback((name: string, value: number) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.map((item) => {
                const { label } = item;
                if (label === name) {
                    return { label, qty: value };
                }
                return item;
            }),
        });
    }, [locationState, setLocationState]);

    const onPipeConnectorChange = useCallback((name: string, value: number) => {
        setLocationState({
            ...locationState,
            pipeConnectors: false,
            selectedAccessories: locationState.selectedAccessories.map((item) => {
                const { label } = item;
                if (label === name) {
                    return { label, qty: value };
                }
                return item;
            }),
        });
    }, [locationState, setLocationState]);

    const deleteAccessory = useCallback((label: string) => {
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.filter((item) => item.label !== label),
        });
    }, [locationState, setLocationState]);

    const onPipeConnectorDelete = useCallback((label: string) => {
        setLocationState({
            ...locationState,
            pipeConnectors: false,
            selectedAccessories: locationState.selectedAccessories.filter((item) => item.label !== label),
        });
    }, [locationState, setLocationState]);

    const removePipeConnectors = useCallback(() => {
        const { isRecommended } = parseRecommended(valve.connectors);
        setLocationState({
            ...locationState,
            selectedAccessories: locationState.selectedAccessories.filter(({ label }) => !isRecommended(label)),
            pipeConnectors: false,
        });
    }, [locationState, setLocationState, valve]);

    const onCommentChange = useCallback((checkoutComment: string) => {
        setLocationState({
            ...locationState,
            checkoutComment,
        });
    }, [locationState, setLocationState]);

    const isCheckoutViewOnly = checkoutViewOnly(userSettings.shoppingCenter);

    const content = buildDesignDataContentRaw({
        valve,
        userInputState,
        region: userSettings.currentRegion,
    });

    const exportData = prepareValvesSelectionExportTable({
        accessories: [
            ...pipeConnectors,
            ...valveAccessories,
            ...actuatorAccessories,
        ],
        actuator,
        combo,
        qty: comboCurrent.qty,
        userInputState,
        userSettings,
        valve,
    });

    const apollo = useApolloClient();

    const checkoutHandler = useCallback(async () => {
        if (!data || inProgress) {
            return;
        }
        const {
            qty = 1,
            mounted,
        } = comboCurrent;

        const comboLabel = comboCurrent.valve === comboCurrent.actuator
            ? comboCurrent.valve
            : `${comboCurrent.valve}${mounted ? '+' : '/'}${comboCurrent.actuator}`;

        setInProgress(true);
        const { result: designCode } = await fetchDesignData({
            content: locationState.sent || isCheckoutViewOnly ? '' : content
                .map((v) => (typeof v !== 'string' ? translate(v.key, v.detectNamespace) : v))
                .join(''),
            apollo,
        });
        if (isRevitPlugin) {
            window.eoapi.extInvoke(
                'MCPluginInsertProduct',
                generateRevitPluginXML(
                    isCombined,
                    comboName,
                    valveAccessories,
                    actuatorName,
                    actuatorAccessories,
                    availableValveParameterIds,
                    availableActuatorParameterIds,
                ),
            );
        } else {
            addToCart([
                {
                    label: comboLabel,
                    qty,
                    ...(designCode && designCode.length > 0 ? { designData: designCode } : {}),
                },
                ...locationState.selectedAccessories,
            ]);

            gaPush([
                {
                    event: 'gaEvent',
                    eventCategory: 'sizing tool',
                    eventAction: 'step 6 - product schedule',
                    eventLabel: 'add to cart or project list',
                },
                {
                    event: 'EEC-addToCart',
                    ecommerce: {
                        currencyCode: currentCurrency,
                        add: {
                            products: [
                                {
                                    name: comboLabel,
                                    id: comboLabel,
                                    price: comboSubtotal,
                                    brand: 'Belimo',
                                    category: 'Valve and actuator',
                                    quantity: qty,
                                    dimension17: [
                                        decodeHtml(data.vssAccessories.valve.family?.raw.join('')),
                                        ...(comboCurrent.valve === comboCurrent.actuator ? [] : [
                                            decodeHtml(data.vssAccessories.actuator?.family?.raw.join('')),
                                        ]),
                                    ].filter((i) => i && i.length > 0).join(' and '),
                                },
                                ...[
                                    ...pipeConnectors,
                                    ...valveAccessories,
                                    ...actuatorAccessories,
                                ].map(({ accessory, price, qty: quantity }) => ({
                                    name: accessory.item.Label,
                                    id: accessory.item.Label,
                                    price: price || 0,
                                    brand: 'Belimo',
                                    category: 'Accessory',
                                    quantity,
                                    dimension17: decodeHtml(accessory.item.description?.raw.join('')),
                                })),
                            ],
                        },
                    },
                }]);
            setLocationState({
                ...locationState,
                sent: true,
            });
            setInProgress(false);
        }
    }, [
        inProgress,
        data,
        comboCurrent,
        locationState,
        isCheckoutViewOnly,
        content,
        apollo,
        gaPush,
        currentCurrency,
        comboSubtotal,
        pipeConnectors,
        valveAccessories,
        actuatorAccessories,
        setLocationState,
        translate,
        isRevitPlugin,
        actuatorName,
        comboName,
        isCombined,
    ]);

    const addToSelection = useCallback(() => {
        if (inProgress) {
            return;
        }
        setInProgress(true);

        // Persisted selections must not carry the transient edit marker in their query.
        const cleanParams = new URLSearchParams(searchParams);
        cleanParams.delete(EDIT_SELECTION_PARAM);
        const searchQuery = cleanParams.toString();

        const existing = isEditMode ? selectionRepository.get(editSelectionName) : null;
        const selectionToSave = {
            type: 'valves' as const,
            createdAt: existing?.createdAt ?? new Date(),
            updatedAt: new Date(),
            name: isEditMode ? editSelectionName : selectionRepository.proposeName(translate(MESSAGES.Selection)),
            content,
            exportData,
            pathname: PATHS.Checkout,
            searchQuery,
            version: '1',
        };

        if (isEditMode) {
            selectionRepository.update(selectionToSave);
        } else {
            selectionRepository.add(selectionToSave);
        }

        persistCallback(selectionRepository.list(selectionRepository.count(), 0)).then(() => {
            navigate({
                pathname: `${location.pathname}/projects/1`,
                search: searchQuery,
            }, {
                replace: true,
            });
            // @TODO handle errors
        })
            // eslint-disable-next-line no-console
            .catch((err) => console.error(err))
            .finally(() => setInProgress(false));
    }, [
        translate,
        location,
        inProgress,
        exportData,
        content,
        selectionRepository,
        persistCallback,
        searchParams,
        navigate,
        isEditMode,
        editSelectionName,
    ]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            {!isRevitPlugin && (
                <Button
                    onClick={addToSelection}
                    size={isMobile ? 'md' : 'sm'}
                    kind="ghost-secondary"
                    icon={(
                        <div className="vss-checkout-page__navigation-icon">
                            {isEditMode ? <SaveIcon/> : <AddToListIcon/>}
                        </div>
                    )}
                >
                    <I18n
                        messageId={isEditMode
                            ? MESSAGES.projectPageSaveSelection
                            : MESSAGES.projectPageAddNewSelection}
                        params={isEditMode ? {
                            name: editSelectionName,
                        } : undefined}
                    />
                </Button>
            )}

            {isRevitPlugin && (
                <ValveDownloadXMLButton
                    isMobile={isMobile}
                    xmlString={generateRevitPluginXML(
                        isCombined,
                        comboName,
                        valveAccessories,
                        actuatorName,
                        actuatorAccessories,
                        availableValveParameterIds,
                        availableActuatorParameterIds,
                    )}
                />
            )}

            <Button
                onClick={checkoutHandler}
                disabled={locationState.sent || isCheckoutViewOnly || inProgress}
                size={isMobile ? 'md' : 'sm'}
                kind="primary"
                icon={(
                    <div className="vss-checkout-page__navigation-icon">
                        {isRevitPlugin ? (<ArrowRightIcon/>) : (<CartIcon/>)}
                    </div>
                )}
            >
                {isRevitPlugin ? (
                    <I18n messageId={MESSAGES.revitPluginInsertButton}/>
                ) : (
                    <I18n messageId={isMobile ? MESSAGES.checkoutMobile : MESSAGES.checkout}/>
                )}
            </Button>
        </NavigationContainer>
    );

    if (locationState.sent) {
        return (
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.selectedProductsTitle}
                    navigation={null}
                    breadcrumbs={null}
                    selectionButton={(
                        <SelectionsButton
                            isMobile={isMobile}
                            searchParams={searchParams}
                            locationState={locationState}
                        />
                    )}
                />

                <SelectedProducts onButtonClick={selectMoreProductsHandler}/>
            </MainLayout>
        );
    }

    return (
        <>
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.checkoutPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <ValveBreadcrumbs
                            currentPage={PATHS.Checkout}
                            homeValue={homePageValue}
                            userInputState={userInputState}
                            locationState={locationState}
                            searchParams={searchParams}
                        />
                    )}
                    selectionButton={!isRevitPlugin && (
                        <SelectionsButton
                            isMobile={isMobile}
                            searchParams={searchParams}
                            locationState={locationState}
                        />
                    )}
                />

                {isCheckoutViewOnly && (
                    <InlineNotification
                        type="warning"
                        message={<I18n messageId={MESSAGES.pleaseContact}/>}
                    />
                )}

                <div className="vss-checkout-page">
                    <h2 className="vss-checkout-page__title">
                        <I18n messageId={MESSAGES.checkoutValveAndActuatorTitle}/>
                    </h2>

                    <Device
                        fullHeight={true}
                        defaultImage={<ValveIcon/>}
                        product={{ ...combo, family: valve.family }}
                        view={isMobile ? 'mobile' : 'default'}
                        attributes={prepareComboAttributes(valve, actuator, prepareVnom)}
                        links={combo && combo.specification ? [] : [
                            valve.specification,
                            ...(actuator ? [actuator?.specification] : []),
                        ]}
                        price={isCheckoutViewOnly ? '\u2014' : (
                            <Price
                                currency={currentCurrency}
                                price={pricePerUnit}
                                locale={userSettings.currentLocale}
                            />
                        )}
                        counter={(
                            <Counter
                                value={comboCurrent.qty}
                                onChange={setComboQty}
                            />
                        )}
                    />

                    {lastSectionShown && (
                        <div className="vss-checkout-page__section">
                            {isDeliveryTypeVisible && (
                                <DeliveryType
                                    variant={variantDecoded}
                                    mounted={comboCurrent.mounted}
                                    onChange={setDeliveryType}
                                    isMobile={isMobile}
                                />
                            )}

                            {pipeConnectors.length > 0 && (
                                <PipeConnectorsView
                                    isSelected={locationState.pipeConnectors}
                                    pipeConnectors={pipeConnectors}
                                    userSettings={userSettings}
                                    currentCurrency={currentCurrency}
                                    isCheckoutViewOnly={isCheckoutViewOnly}
                                    removePipeConnectors={removePipeConnectors}
                                    sensorQty={locationState.combo?.qty || 1}
                                    onChange={onPipeConnectorChange}
                                    onDelete={onPipeConnectorDelete}
                                />
                            )}

                            {isRevitPlugin && is3WayButterflyValveSelected && (
                                <SelectPosition
                                    valvePosition={valvePosition}
                                    setValvePosition={setValvePosition}
                                />
                            )}

                            {isRevitPlugin && isVoltagePresent && (
                                <SelectVoltage
                                    value={nominalVoltage}
                                    onChange={setNominalVoltage}
                                />
                            )}
                        </div>
                    )}

                    <div className="vss-checkout-page__section vss-checkout-page__section-comment">
                        <CheckoutComment
                            labelText={MESSAGES.CheckoutCommentLabel}
                            placeholder={MESSAGES.CheckoutCommentPlaceholder}
                            value={locationState.checkoutComment}
                            onChange={onCommentChange}
                        />
                    </div>

                    <SubTotal
                        isMobile={isMobile}
                        price={isCheckoutViewOnly ? null : comboSubtotal + pipeConnectorsSubtotal}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />

                    {valveAccessories.length > 0 && (
                        <div className="vss-checkout-page__selected-devices-wrapper">
                            <h2 className="vss-checkout-page__subtitle">
                                <I18n messageId={MESSAGES.checkoutValveAccessoriesTitle}/>
                            </h2>

                            <div className="vss-checkout-page__selected-devices">
                                {valveAccessories
                                    .map(({ accessory, price, qty }) => {
                                        const { item } = accessory;
                                        return (
                                            <Accessory
                                                accessory={item}
                                                key={`${item.Label}-${item.ID}`}
                                                price={isCheckoutViewOnly ? '\u2014' : (
                                                    <Price
                                                        price={price}
                                                        currency={currentCurrency}
                                                        locale={userSettings.currentLocale}
                                                    />
                                                )}
                                                counter={(
                                                    <Counter
                                                        value={qty}
                                                        onChange={(value) => setAccessoryQty(item.Label, value)}
                                                    />
                                                )}
                                            >
                                                <div className="vss-checkout-page__action-wrapper">
                                                    <div className="vss-checkout-page__price-wrapper">
                                                        <Button
                                                            kind="ghost-secondary"
                                                            iconOnly={true}
                                                            onClick={() => deleteAccessory(item.Label)}
                                                        >
                                                            <div className="vss-checkout-page__icon-wrapper">
                                                                <DeleteIcon/>
                                                            </div>
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Accessory>
                                        );
                                    })}
                            </div>

                            <SubTotal
                                isMobile={isMobile}
                                price={isCheckoutViewOnly ? null : valveAccessoriesSubtotal}
                                locale={userSettings.currentLocale}
                                currency={currentCurrency}
                            />
                        </div>
                    )}

                    {actuatorAccessories.length > 0 && (
                        <div className="vss-checkout-page__selected-devices-wrapper">
                            <h2 className="vss-checkout-page__subtitle">
                                <I18n messageId={MESSAGES.checkoutActuatorAccessoriesTitle}/>
                            </h2>

                            <div className={clsx({
                                'vss-checkout-page__selected-devices': true,
                                'vss-checkout-page__selected-devices--single': actuatorAccessories.length === 1,
                            })}
                            >
                                {actuatorAccessories
                                    .map(({ accessory, price, qty }) => {
                                        const { item } = accessory;
                                        return (
                                            <Accessory
                                                accessory={item}
                                                key={`${item.Label}-${item.ID}`}
                                                price={isCheckoutViewOnly ? '\u2014' : (
                                                    <Price
                                                        price={price}
                                                        currency={currentCurrency}
                                                        locale={userSettings.currentLocale}
                                                    />
                                                )}
                                                counter={(
                                                    <Counter
                                                        value={qty}
                                                        onChange={(value) => setAccessoryQty(item.Label, value)}
                                                    />
                                                )}
                                            >
                                                <div className="vss-checkout-page__action-wrapper">
                                                    <div className="vss-checkout-page__price-wrapper">
                                                        <Button
                                                            kind="ghost"
                                                            iconOnly={true}
                                                            onClick={() => deleteAccessory(item.Label)}
                                                        >
                                                            <div className="vss-checkout-page__icon-wrapper">
                                                                <DeleteIcon/>
                                                            </div>
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Accessory>
                                        );
                                    })}
                            </div>

                            <SubTotal
                                isMobile={isMobile}
                                price={isCheckoutViewOnly ? null : actuatorAccessoriesSubtotal}
                                locale={userSettings.currentLocale}
                                currency={currentCurrency}
                            />
                        </div>
                    )}

                    <Total
                        price={isCheckoutViewOnly ? null : total}
                        locale={userSettings.currentLocale}
                        currency={currentCurrency}
                    />
                </div>
            </MainLayout>

            {isMobile && navigation}
        </>
    );
};
